import { Box, Stack, Typography, useTheme } from "@mui/material";
import { Button, SelectedCheckCircleIcon } from "components";
import type { PricingPlan } from "../../types";
import { PopularBadgeIcon } from "./pricingAssets";
import { PRICING_COPY } from "./pricingContent";
import { PRICING_DESIGN_VALUES } from "./pricingDesignValues";

interface PlanCardProps {
  plan: PricingPlan;
  onCtaClick: (plan: PricingPlan) => void;
}

export const PlanCard = ({ plan, onCtaClick }: PlanCardProps) => {
  const theme = useTheme();
  const neutralRamp = theme.palette.primitive.neutrals.neutral;
  const isPopular = plan.isPopular === true;

  return (
    <Stack
      sx={{
        gap: "32px",
        minWidth: 0,
        // The annual card rises 60px above the others; all three share a bottom edge.
        marginTop: isPopular ? 0 : "60px",
        padding: "8px",
        border: `1px solid ${isPopular ? neutralRamp[300] : neutralRamp[100]}`,
        borderRadius: "24px",
        backgroundColor: neutralRamp[15],
        overflow: "hidden",
        backdropFilter: PRICING_DESIGN_VALUES.planCardBackdrop,
      }}
    >
      <Stack sx={{ gap: "4px" }}>
        {isPopular && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{
              position: "relative",
              isolation: "isolate",
              overflow: "hidden",
              gap: "4px",
              height: "32px",
              padding: "4px 16px",
              border: `1px solid ${theme.palette.neutral["005"]}`,
              borderRadius: "24px",
              boxShadow: PRICING_DESIGN_VALUES.popularBannerGlass.boxShadow,
              backdropFilter: PRICING_DESIGN_VALUES.popularBannerBackdrop,
              WebkitBackdropFilter: PRICING_DESIGN_VALUES.popularBannerBackdrop,
              // Glass top shade, kept behind the icon and label.
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "inherit",
                pointerEvents: "none",
                zIndex: -1,
                background: PRICING_DESIGN_VALUES.popularBannerGlass.topShade,
              },
            }}
          >
            <PopularBadgeIcon />
            <Typography
              variant="tag-label-xs"
              sx={{ color: theme.palette.primary.main }}
            >
              {PRICING_COPY.mostPopular}
            </Typography>
          </Stack>
        )}

        <Stack
          sx={{
            gap: "16px",
            // Equal summary heights line up the feature lists of the side cards.
            minHeight: isPopular ? "220px" : "236px",
            padding: "24px 24px 32px",
            border: `1px solid ${theme.palette.neutral["005"]}`,
            borderRadius: "24px",
            backgroundColor: theme.palette.neutral.main,
            boxShadow: PRICING_DESIGN_VALUES.shadows.planSummary,
          }}
        >
          <Stack sx={{ gap: "8px" }}>
            <Typography
              variant="h6"
              component="h3"
              sx={{
                ...PRICING_DESIGN_VALUES.titleText,
                color: isPopular
                  ? theme.palette.primary.main
                  : theme.palette.primitive.system.violet[600],
              }}
            >
              {plan.name}
            </Typography>

            <Stack sx={{ gap: "4px" }}>
              <Stack direction="row" alignItems="flex-end" sx={{ gap: "4px" }}>
                <Typography
                  variant="h64"
                  component="span"
                  sx={PRICING_DESIGN_VALUES.priceText}
                >
                  {`$${plan.price}`}
                </Typography>
                {plan.originalPrice !== undefined && (
                  <Typography
                    variant="body-large"
                    component="span"
                    sx={{
                      ...PRICING_DESIGN_VALUES.originalPriceText,
                      color: theme.palette.lightBg.low,
                      textDecoration: "line-through",
                    }}
                  >
                    {`$${plan.originalPrice}`}
                  </Typography>
                )}
                <Typography
                  variant="body-large"
                  component="span"
                  sx={{ color: theme.palette.lightBg.low }}
                >
                  {plan.period}
                </Typography>
              </Stack>
              <Typography
                variant="body-small"
                sx={{ color: theme.palette.lightBg.medium }}
              >
                {plan.note}
              </Typography>
            </Stack>
          </Stack>

          <Button
            variant={plan.ctaVariant}
            fullWidth
            onClick={() => onCtaClick(plan)}
            sx={PRICING_DESIGN_VALUES.buttonOverrides}
          >
            {plan.ctaLabel}
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ paddingInline: "16px" }}>
        <Typography
          variant="caption-2"
          component="p"
          sx={{
            ...PRICING_DESIGN_VALUES.featuresHeadingText,
            marginBottom: "16px",
            color: theme.palette.lightBg.medium,
          }}
        >
          {plan.featuresHeading}
        </Typography>
        <Stack
          component="ul"
          sx={{ gap: "12px", padding: 0, margin: 0, listStyle: "none" }}
        >
          {plan.features.map((feature) => (
            <Stack
              component="li"
              key={feature}
              direction="row"
              alignItems="center"
              sx={{ gap: "12px" }}
            >
              <SelectedCheckCircleIcon
                aria-hidden="true"
                htmlColor={theme.palette.primary.main}
                sx={{ width: "20px", height: "20px", flex: "0 0 20px" }}
              />
              <Typography variant="body-medium">{feature}</Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};
